import React, { useCallback, useEffect, useRef } from "react";
import { useMap } from "react-leaflet";
import { Paper, Box, Typography, IconButton, Divider } from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";

import { onMouseEnter, onMouseLeave } from "@/utils/mapUx";
import { togglePanel } from "@/reducers/panelsReducer";
import VisibilitySwitch from "@/components/ui/VisibilitySwitch";
import PanelResizeHandle from "@/components/ui/PanelResizeHandle";
import {
  PANEL_TITLE_SX,
  PANEL_SUBTITLE_SX,
  PANEL_HEADER_DIVIDER_SX,
} from "@/theme/panelStyles";
import RouteDrillDown from "@/features/routeTree/RouteDrillDown.jsx";
import RouteImportForm from "@/features/route/RouteImportForm.jsx";
import usePanelWidth from "@/hooks/usePanelWidth";
import { publishLeftColumnRight, useLayoutBounds } from "@/features/layout/layoutBounds.jsx";
import { TourHelpButton } from "@/features/tutorials";

export default function VesselPanel({
  isOpen,
  dispatch,
  routeTableOpen,
  markedVesselNames,
  setMarkedVesselNames,
  vesselReportedRoutes,
  fetchingReported,
  switchFleetPlan,
  onToggleFleetPlan,
  fetchingFleetPlan,
  fleetPlanSummary,
  reportedWindowDays,
  setVoyageActive,
  switchVesselFiltersActive,
  setSwitchVesselFiltersActive,
  treeState,
  treeDispatch,
  drillPath,
  setDrillPath,
  drillRequest,
  clearDrillRequest,
  treeData,
  vesselsLoading,
  vesselsError,
  draftRoutes,
  selectedDraftRouteIds,
  onToggleDraftSelected,
  onDiscardDraft,
  onRenameDraft,
  onSaveDraft,
  recenterToVesselLastPosition,
  recenterToRoute,
  onImportDraft,
  onPlanVoyage,
  importDraft,
  importSaving,
  onImportCancel,
  onImportConfirm,
  onImportSelectionHint,
  onRouteDeleted,
  onVoyageDeleted,
  onRouteStateChanged,
  onRecalculateRoute,
  recalculatingRouteId,
  recalcErrorByRouteId,
  onClearRecalcError,
  onOpenRouteTable,
  onCardClose,
}) {
  const map = useMap();
  const panelRef = React.useRef(null);
  const bounds = useLayoutBounds();
  const panelMaxHeight = bounds.panelHeight;

  // Survives the drill-down's unmount on panel switches, else a reopen
  // re-applies old drafts/imports and steals the vessel selection.
  const draftAutoAppliedRef = useRef(new Set());
  const autoDrilledDraftKeysRef = useRef(new Set());
  const autoDrilledVoyagelessKeysRef = useRef(new Set());
  const autoDrilledImportKeyRef = useRef(null);
  const lastAppliedImportRouteIdRef = useRef(null);

  // Panel width is resizable from the right edge (for long voyage/route
  // names). Width stays resizable even when the route table is open; the
  // table docks to the panel's actual right edge and shrinks as it widens.
  const { panelWidth, handleResizeMouseDown } = usePanelWidth({
    storageKey: "vesselPanelWidth",
    map,
    panelRef,
  });

  // While an import awaits confirmation the panel hosts the import form
  // instead of the tree; closing the panel cancels the import (an effect in
  // useRouteImportFlow watches the panel state — panels close each other).
  const importActive = importDraft != null;

  const handleClose = useCallback(() => {
    dispatch(togglePanel("vessel"));
    onMouseLeave(map);
  }, [dispatch]);

  // The table docks against this edge; drawer state and drag width move it.
  useEffect(() => {
    if (!isOpen) return undefined;
    publishLeftColumnRight(bounds.left + panelWidth);
    return () => publishLeftColumnRight(null);
  }, [isOpen, bounds.left, panelWidth]);

  if (!isOpen) return null;

  return (
    <>
      <Paper
        key="vessel-panel"
        id="vessel-panel"
        data-tour="fleet-panel"
        ref={panelRef}
        elevation={0}
        sx={{
          position: "absolute",
          top: bounds.top,
          zIndex: 1000,
          bgcolor: "background.paper",
          width: `${panelWidth}px`,
          color: "#004f7d",
          borderRadius: "0px",
          marginLeft: `${bounds.left}px`,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          height: `${panelMaxHeight}px`,
          boxSizing: "border-box",
          p: 1.5,
        }}
        onMouseEnter={() => {
          setVoyageActive(false);
          onMouseEnter(map);
        }}
        onMouseLeave={() => {
          setVoyageActive(true);
          onMouseLeave(map);
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 1.5,
            flexShrink: 0,
          }}
        >
          <Box>
            <Typography sx={PANEL_TITLE_SX}>
              {importActive ? "Import Plan Route" : "Fleet Explorer"}
            </Typography>
            <Typography sx={PANEL_SUBTITLE_SX}>
              {importActive
                ? "Preview on map — confirm to save"
                : "Vessels, voyages & routes"}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <TourHelpButton tourId="route-types" />
            <VisibilitySwitch
              checked={switchVesselFiltersActive}
              onChange={(e) => setSwitchVesselFiltersActive(e.target.checked)}
              slotProps={{ input: { "aria-label": "toggle fleet explorer visibility" } }}
            />
            <IconButton
              aria-label="close"
              onClick={handleClose}
              sx={{ color: "#004f7d" }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>

        <Divider sx={{ ...PANEL_HEADER_DIVIDER_SX, flexShrink: 0 }} />

        {importActive ? (
          <RouteImportForm
            draftRoute={importDraft}
            saving={importSaving}
            onCancel={onImportCancel}
            onConfirm={onImportConfirm}
          />
        ) : (
          <RouteDrillDown
            isOpen={isOpen}
            treeState={treeState}
            treeDispatch={treeDispatch}
            drillPath={drillPath}
            setDrillPath={setDrillPath}
            drillRequest={drillRequest}
            clearDrillRequest={clearDrillRequest}
            treeData={treeData}
            vesselsLoading={vesselsLoading}
            vesselsError={vesselsError}
            draftRoutes={draftRoutes}
            draftAutoAppliedRef={draftAutoAppliedRef}
            autoDrilledDraftKeysRef={autoDrilledDraftKeysRef}
            autoDrilledVoyagelessKeysRef={autoDrilledVoyagelessKeysRef}
            autoDrilledImportKeyRef={autoDrilledImportKeyRef}
            lastAppliedImportRouteIdRef={lastAppliedImportRouteIdRef}
            selectedDraftRouteIds={selectedDraftRouteIds}
            onToggleDraftSelected={onToggleDraftSelected}
            onDiscardDraft={onDiscardDraft}
            onRenameDraft={onRenameDraft}
            onSaveDraft={onSaveDraft}
            markedVesselNames={markedVesselNames}
            setMarkedVesselNames={setMarkedVesselNames}
            recenterToVesselLastPosition={recenterToVesselLastPosition}
            recenterToRoute={recenterToRoute}
            switchVesselFiltersActive={switchVesselFiltersActive}
            switchFleetPlan={switchFleetPlan}
            onToggleFleetPlan={onToggleFleetPlan}
            fetchingFleetPlan={fetchingFleetPlan}
            fleetPlanSummary={fleetPlanSummary}
            reportedWindowDays={reportedWindowDays}
            fetchingReported={fetchingReported}
            vesselReportedRoutes={vesselReportedRoutes}
            onImportDraft={onImportDraft}
            onPlanVoyage={onPlanVoyage}
            onImportSelectionHint={onImportSelectionHint}
            onRouteDeleted={onRouteDeleted}
            onVoyageDeleted={onVoyageDeleted}
            onRouteStateChanged={onRouteStateChanged}
            onRecalculateRoute={onRecalculateRoute}
            recalculatingRouteId={recalculatingRouteId}
            recalcErrorByRouteId={recalcErrorByRouteId}
            onClearRecalcError={onClearRecalcError}
            onOpenRouteTable={onOpenRouteTable}
            onCardClose={onCardClose}
          />
        )}
      </Paper>

      {/* Stays active with the route table open; the table follows the
          panel's right edge. */}
      <PanelResizeHandle
        panelWidth={panelWidth}
        panelMaxHeight={panelMaxHeight}
        onMouseDown={handleResizeMouseDown}
      />
    </>
  );
}
